import { Avatar, IconButton, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Table } from '@mui/material';
import { TableBody } from '@mui/material';
import { Box } from '@mui/material';
import styles from '../../styles/styles';
import { FriendlyFormatNumber } from '../../Utils';
import { useLiquidInstanceContext } from '../../context/liquidInstanceContext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
interface YieldRowProps {
  chain: string;
  yieldSourceApp: string;
  yieldSourceName: string;
  yieldSourceDescription: string;
  yieldSourceURL: string;
  yieldSourceCategory: string;
  TVL: string;
  APY: number;
}
// Constants
const CELL_BACKGROUND = 'rgba(255, 255, 255, 0.09)';
const HOVER_BACKGROUND = 'rgba(255, 255, 255, 0.15)';

function YieldRow(row: YieldRowProps) {
  return (
    <TableRow
      sx={{
        '&:hover': {
          '& .MuiTableCell-root': {
            background: `${HOVER_BACKGROUND} !important`
          },
          '& .circle-background': {
            opacity: '1 !important'
          }
        },
        '& .MuiTableCell-root': {
          border: 'none'
        },
        '& td:not(:first-of-type):not(:last-of-type)': {
          background: CELL_BACKGROUND
        },
        '& td:first-of-type, & td:last-of-type': {
          background: CELL_BACKGROUND,
          borderRadius: '1.5625rem'
        },
        '& td:first-of-type': {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        },
        '& td:nth-of-type(2)': {
          borderTopLeftRadius: { xs: '1.5625rem', md: 0 },
          borderBottomLeftRadius: { xs: '1.5625rem', md: 0 }
        },
        '& td:last-of-type': {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }
      }}
    >
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{ width: { xs: 20, md: 25 }, height: { xs: 20, md: 25 } }}
            src={`/l2/instances/${row.yieldSourceApp.toLowerCase()}.svg`}
          />
          {row.yieldSourceApp}
        </Box>
      </TableCell>
      <TableCell>
        {' '}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.yieldSourceDescription}
          <IconButton size="small" onClick={() => window.open(row.yieldSourceURL, '_blank')}>
            <OpenInNewIcon sx={{ color: 'grey', fontSize: 12 }} />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.yieldSourceCategory}</TableCell>
      <TableCell>{'$' + FriendlyFormatNumber(Number(row.TVL))}</TableCell>
      <TableCell>{(row.APY * 10000).toFixed(2) + '%'}</TableCell>
    </TableRow>
  );
}

export default function YieldTable({ yieldDataToDisplay }: { yieldDataToDisplay: YieldRowProps[] }) {
  const { liquidInstanceConfiguration } = useLiquidInstanceContext();

  // Sort the data by APY and TVL in descending order
  const sortedYieldData = [...yieldDataToDisplay].sort((a, b) => {
    // First sort by APY
    const apyDiff = b.APY - a.APY;
    if (apyDiff !== 0) return apyDiff;

    // If APY is equal, sort by TVL
    return Number(b.TVL) - Number(a.TVL);
  });

  return (
    <Box sx={{ ...styles.boxBG, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          sx={{ width: { xs: 30, md: 40 }, height: { xs: 30, md: 40 } }}
          src={`/l2/${liquidInstanceConfiguration.L2.name.toLowerCase()}.svg`}
        />
        <Typography variant="h6">{liquidInstanceConfiguration.L2.L2TokenName} Yields</Typography>
      </Box>
      <TableContainer
        sx={{
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: '0 0.625rem'
          },
          overflow: 'hidden'
        }}
      >
        <Table sx={{ minWidth: { xs: 300, md: 650 } }} aria-label="yields table">
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  border: 'none',
                  backgroundColor: CELL_BACKGROUND
                },
                '& .MuiTableCell-root:first-of-type': {
                  borderTopLeftRadius: '1.5625rem',
                  borderBottomLeftRadius: '1.5625rem'
                },
                '& .MuiTableCell-root:last-of-type': {
                  borderTopRightRadius: '1.5625rem',
                  borderBottomRightRadius: '1.5625rem'
                },
                '& .MuiTableCell-root:nth-of-type(2)': {
                  borderTopLeftRadius: { xs: '1.5625rem', md: 0 },
                  borderBottomLeftRadius: { xs: '1.5625rem', md: 0 }
                }
              }}
            >
              <TableCell sx={{ ...styles.greyText, display: { xs: 'none', md: 'table-cell' } }}>App</TableCell>
              <TableCell sx={{ ...styles.greyText }}>Desc</TableCell>
              <TableCell sx={{ ...styles.greyText, display: { xs: 'none', md: 'table-cell' } }}>Category</TableCell>
              <TableCell sx={{ ...styles.greyText }}>TVL</TableCell>
              <TableCell sx={{ ...styles.greyText }}>APY</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedYieldData.map((row, i) => (
              <YieldRow key={row.yieldSourceName + i} {...row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
